/**
 * Where "I have read this notification" is stored — per PERSON, not per browser.
 *
 * The bell and the Activity feed share one read-state key: mark an alert read in
 * the menu and the feed must agree, and `@authe/notification-center` seeds its
 * read-id set from that key on mount, stamps `read:` from it on every ingest,
 * and counts the unread badge from it. Both surfaces passed the literal
 * `"dashboard.notifications.read"`, which is ONE keyspace for every user of the
 * browser — so on a shared analyst workstation the second analyst's bell showed
 * the first analyst's critical alerts as already read and left them out of the
 * unread count. Server-side read state is posted but never read back
 * (`toNotification` defaults `read: false`), so this key is the only record.
 *
 * The key is derived through `ScopedStorage`, not hand-assembled, so it is the
 * same namespacing every other stored preference uses — and it is derived HERE,
 * once, because two surfaces sharing a key is the point: a second spelling of it
 * would silently split the bell's read state from the feed's.
 *
 * The controller reads the key once, at mount, so a surface should also use it as
 * its `key` — see `NotificationsMenu`.
 */
import * as React from "react";
import { createScopedStorage } from "@authe/fe-commons/storage";
import { useStorageIdentity } from "./storageIdentity";

/** Name under the identity namespace. The tiers are `ScopedStorage`'s to add. */
const READ_STATE_NAME = "notifications.read";

/** The identity-scoped localStorage key for notification read state. */
export function useNotificationsReadKey(): string {
  const identity = useStorageIdentity();
  const { app, customerId, orgId, tenantId, userId } = identity;
  return React.useMemo(() => {
    const scope = createScopedStorage({ app, customerId, orgId, tenantId, userId });
    // The narrowest tier this identity supports: `user` once somebody is signed
    // in, and the tenant/org/app tier while the session is still resolving —
    // never a key that outlives the person it belongs to.
    return scope.keyAt(READ_STATE_NAME, scope.defaultLevel);
    // Memoised on the FIELDS: `useStorageIdentity` returns a fresh literal.
  }, [app, customerId, orgId, tenantId, userId]);
}
